import {
  AllowedUploadMimeType,
  AllowedUploadMimeTypeLabel,
  defaultAcceptTypes,
  isAllowedFilename,
} from 'corso-types';
import { fileTypeFromBlob } from 'file-type';
import {
  ChangeEventHandler,
  FormEventHandler,
  PropsWithChildren,
  useCallback,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { twMerge } from 'tailwind-merge';
import { match } from 'ts-pattern';
import { z } from 'zod';
import Button from '~/components/button/Button';
import IconButton from '~/components/button/IconButton';
import Divide from '~/components/Divide';
import Icon from '~/components/Icon';
import MediaDisplay, { MediaFallback } from '~/components/MediaDisplay';
import {
  Modal,
  ModalFooter,
  ModalHeader,
  ModalTitle,
} from '~/components/Modal';
import Spinner from '~/components/Spinner';
import api from '~/utils/api';
import { uniqueId, useUniqueId } from '~/utils/compute';
import { disjunctiveList } from '~/utils/formatters';
import { logError } from '~/utils/observability';
import { mediaAssetSchema } from '~/utils/schemas';
import Field from './Field';

type UploadFilesParams = Parameters<typeof api.uploadFile>[0];
type APIUploadResult = Awaited<ReturnType<typeof api.uploadFile>>;
type MediaAsset = z.infer<typeof mediaAssetSchema>;
type BaseAsset = Omit<MediaAsset, 'cdnName'> & { id: string };

type UploadedAsset = BaseAsset & {
  status: 'uploaded';
  /** The original filename. */
  name: string;
  /** The filename as uploaded to the CDN. */
  cdnName: string;
};
type PreviewAsset = Omit<BaseAsset, 'src'> & {
  status: 'preview';
  file: File;
};
type ErrorAsset = Omit<PreviewAsset, 'status'> & {
  status: 'error';
  error: Error;
};
type InvalidAsset = Omit<ErrorAsset, 'status'> & {
  status: 'invalid';
};
type Asset = UploadedAsset | PreviewAsset | ErrorAsset | InvalidAsset;

type AcceptType = AllowedUploadMimeType;

const useCaptureSupported = () => {
  const captureSupported = useRef(false);

  useLayoutEffect(() => {
    const input = document.createElement('input');

    captureSupported.current = 'capture' in input;
  }, []);

  return captureSupported.current;
};

type DropzoneProps = PropsWithChildren<{
  onChange: (files: FileList) => Promise<void>;
  accept: AcceptType[] | Readonly<AcceptType[]>;
  captureMedia: 'image' | 'video' | undefined;
  required?: boolean;
  describedby?: string;
  id: string;
  invalid?: boolean;
}>;

function Dropzone({
  required,
  onChange,
  accept,
  captureMedia,
  describedby,
  id,
  invalid,
  children,
}: DropzoneProps) {
  const [changePending, setChangePending] = useState(false); // used to temporarily disable inputs while evaluating files
  const dropzoneId = useUniqueId();
  const captureSupported = useCaptureSupported();
  const cameraInputRef = useRef<HTMLInputElement>(null);

  const handleChange = useCallback(
    (files: FileList) => {
      setChangePending(true);
      // oxlint-disable-next-line typescript/no-floating-promises -- ignored as we would not want to await this handler
      onChange(files).finally(() => {
        setChangePending(false);
      });
    },
    [onChange],
  );

  const handleInputChange = useCallback<ChangeEventHandler<HTMLInputElement>>(
    (event) => {
      const { target } = event;

      if (target.files) {
        handleChange(target.files);
        target.value = ''; // reset the input value to allow re-uploading the same file
        target.files = null; // clear the files to avoid re-triggering the change event
      }
    },
    [handleChange],
  );

  return (
    <div
      id={dropzoneId}
      data-component="corso-dropzone"
      className={twMerge(
        'relative flex w-full flex-col items-center justify-center gap-4 rounded-lg border border-dashed border-outline bg-surface-container-low p-4 body-md text-on-surface-variant shadow-(--field-shadow) focus-within:border-brand focus-within:brightness-97 hover:brightness-97',
        invalid &&
          'border-critical focus:border-critical focus:outline-critical',
      )}
    >
      <div
        className={twMerge(
          'flex w-full flex-col items-center justify-center gap-4',
          captureSupported && 'relative',
        )}
      >
        <Icon icon="CloudUploadSolid" className="size-10" />
        <span className="ml-3">
          Drag and drop your files here or click to browse
        </span>

        <input
          // oxlint-disable-next-line jsx-a11y/prefer-tag-over-role --  browsers put this role on, but seems like jsdom doesn't
          role="button"
          data-testid="fileInput"
          id={id}
          aria-describedby={`${describedby} ${dropzoneId}`}
          multiple
          className="absolute inset-0 cursor-pointer opacity-0"
          type="file"
          accept={accept.join(', ')}
          onDrop={(event) => {
            event.preventDefault();
            handleChange(event.dataTransfer.files);
          }}
          onChange={handleInputChange}
          disabled={changePending}
        />
      </div>
      {captureSupported && captureMedia && (
        <div className="relative w-full border-t border-t-outline pt-4 after:absolute after:inset-x-0 after:-top-3 after:mx-auto after:w-fit after:bg-surface-container-low after:px-2 after:content-['or']">
          <Button
            variant="outlined"
            className="flex w-full items-center gap-2"
            onClick={() => cameraInputRef.current?.click()}
          >
            <Icon icon="CameraSolid" />
            {captureMedia === 'image' ? 'Take a Photo' : 'Record a Video'}
          </Button>
          <input
            ref={cameraInputRef}
            data-testid="cameraInput"
            type="file"
            capture="environment"
            accept={accept
              .filter((type) => type.startsWith(`${captureMedia}/`))
              .join(', ')}
            className="hidden"
            onChange={handleInputChange}
            disabled={changePending}
          />
        </div>
      )}
      {children}
      {required && <span className="sr-only">required</span>}
    </div>
  );
}

type AssetDisplayProps = {
  asset: Asset;
  onRemove: (asset: Asset) => void;
  onRetry: (asset: ErrorAsset) => void;
};

function AssetItem({ asset, onRemove, onRetry }: AssetDisplayProps) {
  const [showConfirm, setShowConfirm] = useState(false);
  const closeConfirm = () => setShowConfirm(false);
  const removeAsset = () => {
    onRemove(asset);
    closeConfirm();
  };

  return (
    <li className="flex items-center justify-between gap-2">
      <figure className="flex min-w-0 items-center gap-2 md:gap-4">
        {match(asset)
          .with({ status: 'uploaded' }, (a) => (
            <MediaDisplay
              expandable
              src={a.src}
              type={a.type}
              className="size-8 md:size-12"
              alt={a.name}
            />
          ))
          .otherwise((a) => (
            <MediaFallback
              type={a.file.type}
              className="size-8 md:size-12"
              alt={asset.name}
            />
          ))}

        <figcaption className="flex min-w-0 flex-col gap-1">
          <div className="truncate body-sm text-on-surface-variant">
            {asset.name}
          </div>
          {(asset.status === 'error' || asset.status === 'invalid') && (
            <div role="alert" className="flex gap-1 body-sm text-critical">
              <Icon icon="ExclamationCircleSolid" className="mt-0.5 size-4" />
              {asset.error.message}
            </div>
          )}
        </figcaption>
      </figure>

      {match(asset)
        .with({ status: 'preview' }, (previewAsset) => (
          <Spinner
            className="fill-black size-9 p-1"
            description={`Uploading ${previewAsset.name}`}
          />
        ))
        .with(
          { status: 'uploaded' },
          { status: 'invalid' },
          (uploadedAsset) => (
            <IconButton
              icon="Trash"
              aria-label={`Remove ${uploadedAsset.name}`}
              onClick={() => setShowConfirm(true)}
            />
          ),
        )
        .with({ status: 'error' }, (errorAsset) => (
          <IconButton
            icon="ArrowsRotate"
            aria-label="Retry"
            onClick={() => onRetry(errorAsset)}
          />
        ))
        .exhaustive()}

      <Modal open={showConfirm} onClose={closeConfirm}>
        <ModalHeader>
          <ModalTitle>Remove File</ModalTitle>
        </ModalHeader>
        <p className="body-md">Do you want to remove the file {asset.name}?</p>
        <ModalFooter>
          <Button variant="outlined" onClick={closeConfirm}>
            Keep
          </Button>
          <Button className="flex items-center gap-2" onClick={removeAsset}>
            <Icon icon="Trash" className="size-5" /> Remove
          </Button>
        </ModalFooter>
      </Modal>
    </li>
  );
}

const uploadFile =
  (params: {
    storefrontId: UploadFilesParams['params']['storefrontId'];
    destination: UploadFilesParams['query']['destination'];
    onSuccess: (params: {
      asset: PreviewAsset;
      result: APIUploadResult;
    }) => void;
    onError: (params: { asset: PreviewAsset; result: Error }) => void;
  }) =>
  (asset: PreviewAsset) => {
    api
      .uploadFile({
        params: { storefrontId: params.storefrontId },
        query: { destination: params.destination },
        body: asset.file,
      })
      .then((result) => {
        params.onSuccess({ asset, result });
      })
      .catch((e) => {
        const error =
          e instanceof Error ? e : new Error('Upload failed', { cause: e });

        logError(error, { extra: { name: asset.name, type: asset.type } });

        params.onError({
          asset,
          result: new Error('Failed to upload file'),
        });
      });
  };

const upsertAsset = (assets: Asset[], asset: Asset) => {
  const next = [...assets];
  const index = next.findIndex((a) => a.id === asset.id);

  if (index !== -1) {
    next[index] = asset;
  } else {
    next.push(asset);
  }

  return next;
};

const fileMessaging = (count: number, noun: string) =>
  `${count} ${noun}${count === 1 ? '' : 's'}`;

const getMinMaxMessage = (minMax: { min: number; max: number }, noun: string) =>
  match(minMax)
    .with({ min: 0 }, () => '')
    .when(
      ({ min, max }) => min === max,
      ({ min }) => `Upload ${fileMessaging(min, noun)}`,
    )
    .with(
      { max: Infinity },
      ({ min }) => `Upload at least ${fileMessaging(min, noun)}`,
    )
    .otherwise(
      ({ min, max }) => `Upload ${min} to ${fileMessaging(max, noun)}`,
    );

type State = {
  assets: Asset[];
  isValid: boolean;
  uploadedCount: number;
};

type Props = {
  label: string;
  className?: string;
  name: string;
  storefrontId: UploadFilesParams['params']['storefrontId'];
  destination: UploadFilesParams['query']['destination'];
  min?: number;
  max?: number;
  defaultValue?: Omit<UploadedAsset, 'id' | 'status'>[];
  description?: string;
  accept?: AcceptType[] | Readonly<AcceptType[]>;
};

export default function FileUploader({
  label,
  className,
  name,
  storefrontId,
  destination,
  min: minUploads = 0,
  max: maxUploads = Infinity,
  defaultValue = [],
  description,
  accept = defaultAcceptTypes,
}: Props) {
  const [state, setState] = useState<State>({
    assets: defaultValue.map((asset) => ({
      ...asset,
      // give existing asset an id for display list
      id: uniqueId(),
      status: 'uploaded',
    })),
    isValid: true,
    uploadedCount: defaultValue.length,
  });

  const descriptionId = useUniqueId();
  const errorId = useUniqueId();
  const dropzoneId = useUniqueId();
  const requiredCountValidityRef = useRef<HTMLInputElement>(null);
  const acceptedMedia = new Set(accept.map((type) => type.split('/')[0]));
  const captureMedia = (['image', 'video'] as const).find((media) =>
    acceptedMedia.has(media),
  );
  const fileNoun =
    acceptedMedia.size === 1 && captureMedia ? `${captureMedia} file` : 'file';

  const handleInvalid: FormEventHandler<HTMLInputElement> = (event) => {
    const msg = match(event.currentTarget.validity)
      .with(
        { rangeOverflow: true },
        () =>
          `Please upload a maximum of ${fileMessaging(maxUploads, fileNoun)}.`,
      )
      .with(
        { rangeUnderflow: true },
        () => `Please upload at least ${fileMessaging(minUploads, fileNoun)}.`,
      )
      .otherwise(
        () => `Please upload at least ${fileMessaging(minUploads, fileNoun)}.`,
      );

    event.currentTarget.setCustomValidity(msg);

    setState((prev) => ({
      ...prev,
      isValid: false,
    }));
  };

  const handleAssetChange = (
    asset: Asset,
    action: 'upsert' | 'remove' = 'upsert',
  ) => {
    setState((prev) => {
      const updatedAssets =
        action === 'upsert'
          ? upsertAsset(prev.assets, asset)
          : prev.assets.filter((a) => a.id !== asset.id);

      const uploadedCount = updatedAssets.filter(
        (a) => a.status === 'uploaded',
      ).length;

      // updating the validity on asset change only when the state is invalid to provide a delayed validation until submit
      const isValid = !prev.isValid
        ? uploadedCount >= minUploads && uploadedCount <= maxUploads
        : prev.isValid;

      // reset validity if invalid and now valid
      if (!prev.isValid && isValid && requiredCountValidityRef.current) {
        requiredCountValidityRef.current.setCustomValidity('');
      }

      return {
        assets: updatedAssets,
        isValid,
        uploadedCount,
      } satisfies State;
    });
  };

  const uploadAsset = uploadFile({
    storefrontId,
    destination,
    onSuccess: ({ asset: { file, ...asset }, result }) => {
      handleAssetChange({
        id: asset.id,
        type: asset.type,
        src: result.src,
        name: asset.name,
        cdnName: result.name,
        status: 'uploaded',
      } satisfies UploadedAsset);
    },
    onError: ({ asset, result }) =>
      handleAssetChange({
        ...asset,
        status: 'error',
        error: result,
      } satisfies ErrorAsset),
  });

  const onUpload = async (fileList: FileList) => {
    await Promise.all(
      Array.from(fileList).map(async (file) => {
        const asset = {
          id: uniqueId(),
          name: file.name,
          type: file.type,
          file,
          status: 'preview',
        } satisfies PreviewAsset;

        const fileType = await fileTypeFromBlob(file);
        if (!fileType) {
          handleAssetChange({
            ...asset,
            status: 'invalid',
            error: new Error('Could not determine file type.'),
          });
          return;
        }

        asset.type = fileType.mime; // trust the file bytes for the type, browser `file.type` seems to be unreliable and more based on the extension

        // even if a extension vs mime type mismatch occurs, so long as the bytes are an accepted type we will upload as that inspected type
        if (!accept.includes(asset.type)) {
          // could inform differently unaccepted files with mismatched types
          handleAssetChange({
            ...asset,
            status: 'invalid',
            error: new Error(
              `File not accepted. Accepted file types: ${disjunctiveList(accept.map((type) => AllowedUploadMimeTypeLabel[type]))}.`,
            ),
          });
          return;
        }

        if (!isAllowedFilename(asset.name)) {
          // * intentionally capturing invalid filenames for easier monitoring and debugging
          logError(new Error('Invalid Filename'), {
            extra: { name: asset.name, type: asset.type },
          });
          handleAssetChange({
            ...asset,
            status: 'invalid',
            error: new Error('Filename not allowed.'),
          });
          return;
        }

        // add asset to state to display preview while uploaded
        handleAssetChange(asset);
        uploadAsset(asset);
      }),
    );
  };

  const removeAsset = (asset: Asset) => {
    handleAssetChange(asset, 'remove');
  };

  const retryUpload = ({ error, ...rest }: ErrorAsset) => {
    const retryAsset = {
      ...rest,
      status: 'preview',
    } satisfies PreviewAsset;

    handleAssetChange(retryAsset);
    uploadAsset(retryAsset);
  };

  const hasMinMaxRequirements = minUploads > 0 || maxUploads < Infinity;

  const minMaxMessage = getMinMaxMessage(
    { min: minUploads, max: maxUploads },
    fileNoun,
  );

  return (
    <Field.Root className={className}>
      <Field.Label htmlFor={dropzoneId}>
        {label} {minUploads === 0 && ` (optional)`}
      </Field.Label>
      {description && (
        <Field.HelpText className="mb-1" id={descriptionId}>
          {description}
        </Field.HelpText>
      )}

      <Dropzone
        onChange={onUpload}
        required={minUploads > 0}
        accept={accept}
        captureMedia={captureMedia}
        id={dropzoneId}
        describedby={[descriptionId, errorId].join(' ')}
      >
        {hasMinMaxRequirements && (
          <span className={state.isValid ? '' : 'text-critical'}>
            {minMaxMessage}
          </span>
        )}
      </Dropzone>
      {hasMinMaxRequirements && !state.isValid && (
        <Field.ErrorText id={errorId} className="mt-1">
          {requiredCountValidityRef.current?.validationMessage}
        </Field.ErrorText>
      )}

      <input
        id="requiredCountValidity"
        ref={requiredCountValidityRef}
        type="number"
        // using hidden attribute will prevent input from participating in validation aria-hidden will remove it from the accessibility tree
        aria-hidden
        tabIndex={-1}
        className="sr-only"
        min={minUploads}
        max={maxUploads}
        onInvalid={handleInvalid}
        value={state.uploadedCount}
        // noop to avoid uncontrolled input warning
        onChange={() => {}}
      />
      {state.assets.length > 0 && (
        // * using a single column grid to handle long filenames with truncation better in the available space
        <Divide as="ul" spacing="compact" className="grid w-full grid-cols-1">
          {state.assets.map((asset) => (
            <AssetItem
              key={asset.id}
              asset={asset}
              onRemove={removeAsset}
              onRetry={retryUpload}
            />
          ))}
        </Divide>
      )}
      {state.assets
        .filter((a) => a.status === 'uploaded')
        .map(({ id, status, ...asset }) => (
          <input
            key={id}
            hidden
            name={name}
            defaultValue={JSON.stringify(asset satisfies MediaAsset)}
          />
        ))}
    </Field.Root>
  );
}
